import React from 'react';
import PropTypes from 'prop-types';
import * as Dialog from '@radix-ui/react-dialog';
import { useScreenSize } from '@/hooks';
import styles from './Modal.module.css';

/**
 * Modal Component
 * Reusable modal using Radix UI Dialog with transparent backdrop and blur
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal should close
 * @param {string} title - Modal title (optional)
 * @param {ReactNode} children - Modal content
 * @param {string} size - Modal size: 'sm' | 'md' | 'lg' | 'xl'
 * @param {boolean} showCloseButton - Show close button (default: true)
 * @param {ReactNode} headerContent - Custom header content (e.g., logo)
 * @param {string} className - Additional CSS classes
 * @param {string} bgColor - Custom background color/gradient (optional)
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  headerContent,
  className = '',
  bgColor,
}) => {
  const { isMobile, isTablet } = useScreenSize();
  const isFullScreen = isMobile || isTablet;

  const modalStyle = bgColor
    ? { background: bgColor }
    : {};

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.backdrop} />
        <Dialog.Content
          className={`${styles.modal} ${styles[size]} ${isFullScreen ? styles.mobileFullScreen : ''} ${className}`}
          style={modalStyle}
        >
          {(title || showCloseButton || headerContent) && (
            <div className={`${styles.header} ${headerContent ? styles.headerCentered : ''}`}>
              {headerContent ? (
                <>
                  <div className={styles.headerSpacer} aria-hidden="true" />
                  <div className={styles.headerCenter}>
                    {headerContent}
                  </div>
                </>
              ) : null}
              {title && !headerContent && (
                <h2 id="modal-title" className={styles.title}>
                  {title}
                </h2>
              )}
              {showCloseButton && (
                <Dialog.Close className={styles.closeButton} aria-label="Close modal">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Dialog.Close>
              )}
            </div>
          )}

          <div className={styles.content}>{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  showCloseButton: PropTypes.bool,
  headerContent: PropTypes.node,
  className: PropTypes.string,
  bgColor: PropTypes.string,
};

export default Modal;
